import React, { useEffect, useMemo, useState } from 'react';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import { useToast } from '../../common/ToastSystem';
import {
  allocateEventGuestTicketsToAllParticipants,
  changeEventGuestTicketRegistrationStatus,
  createEventGuestTicketRegistration,
  getEventGuestTicketDashboard,
  publishEventGuestTicketAnnouncement,
  updateEventGuestTicketSettings,
  updateEventGuestTicketRegistration,
  upsertEventGuestTicketAllocation,
  type EventGuestTicketAdminDashboard,
  type EventGuestTicketRegistration,
} from '../../../api/taskEvents';
import '../../../css/admin/events/EventGuestTicketManagement.css';

type Props = {
  eventUuid: string;
  canView: boolean;
  canEdit: boolean;
  canManageAllocations: boolean;
};

type TabKey = 'registrations' | 'allocations' | 'settings';

type GuestForm = {
  guestName: string;
  guestNameKana: string;
  phone: string;
  email: string;
  relationship: string;
  affiliation: string;
  seatNote: string;
  accessibilityNote: string;
  registrationNote: string;
};

const EMPTY_GUEST_FORM: GuestForm = {
  guestName: '',
  guestNameKana: '',
  phone: '',
  email: '',
  relationship: '',
  affiliation: '',
  seatNote: '',
  accessibilityNote: '',
  registrationNote: '',
};

const STATUS_LABELS: Record<string, string> = {
  reserved: '取り置き・未精算',
  paid: '支払済み',
  no_show: '未来場',
  registered: '招待登録',
  used: '来場済み',
  cancelled: '取消済み',
};

const FIELD_LABELS: Record<string, string> = {
  guestName: '名前',
  guestNameKana: 'ふりがな',
  phone: '電話番号',
  email: 'メールアドレス',
  relationship: '招待者との関係',
  affiliation: '所属',
  seatNote: '座席情報・メモ',
  accessibilityNote: '配慮事項',
  registrationNote: '参加者に見えるメモ',
};

const toLocalDateTime = (value: string | null | undefined) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const toOptionalIso = (value: string) => value ? new Date(value).toISOString() : null;

const formatNumber = (value: unknown) => Number(value || 0).toLocaleString('ja-JP');

export default function EventGuestTicketManagementModal({
  eventUuid,
  canView,
  canEdit,
  canManageAllocations,
}: Props) {
  const toast = useToast() as { success: (text: string) => void; error: (text: string) => void };
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState<EventGuestTicketAdminDashboard | null>(null);
  const [tab, setTab] = useState<TabKey>('registrations');
  const [kind, setKind] = useState<'hold' | 'invite'>('hold');
  const [ticketTypeId, setTicketTypeId] = useState('');
  const [inviterArtistId, setInviterArtistId] = useState('');
  const [guestForm, setGuestForm] = useState<GuestForm>(EMPTY_GUEST_FORM);
  const [internalNote, setInternalNote] = useState('');
  const [editingRegistrationId, setEditingRegistrationId] = useState<string | null>(null);
  const [allocationArtistId, setAllocationArtistId] = useState('');
  const [allocationTicketTypeId, setAllocationTicketTypeId] = useState('');
  const [holdLimit, setHoldLimit] = useState('0');
  const [inviteLimit, setInviteLimit] = useState('0');
  const [allocationDeadline, setAllocationDeadline] = useState('');
  const [settingsDeadline, setSettingsDeadline] = useState('');
  const [settingsFields, setSettingsFields] = useState<EventGuestTicketAdminDashboard['settings']['formFields']>([]);
  const [paymentTarget, setPaymentTarget] = useState<EventGuestTicketRegistration | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('0');
  const [cancellationTarget, setCancellationTarget] = useState<EventGuestTicketRegistration | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const next = await getEventGuestTicketDashboard(eventUuid);
      setDashboard(next);
      setSettingsDeadline(toLocalDateTime(next.settings.registrationDeadlineAt));
      setSettingsFields(next.settings.formFields);
      const firstTicket = next.summary.ticketTypes[0];
      setTicketTypeId((current) => current || String(firstTicket?.ticketTypeId || ''));
      setAllocationTicketTypeId((current) => current || String(firstTicket?.ticketTypeId || ''));
      setAllocationArtistId((current) => current || String(next.eligibleParticipants[0]?.artistId || ''));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '取り置き・招待情報を取得できませんでした');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !canView) return;
    void load();
    // open/eventUuid の変更時だけ再取得する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventUuid, canView]);

  const selectedTicket = useMemo(
    () => dashboard?.summary.ticketTypes.find((ticket) => String(ticket.ticketTypeId) === ticketTypeId) || null,
    [dashboard?.summary.ticketTypes, ticketTypeId]
  );

  const resetRegistrationForm = () => {
    setGuestForm(EMPTY_GUEST_FORM);
    setInternalNote('');
    setEditingRegistrationId(null);
  };

  const enabledRegistrationFields = useMemo(
    () => (dashboard?.settings.formFields || []).filter((field) => field.enabled && field.key in EMPTY_GUEST_FORM),
    [dashboard?.settings.formFields]
  );

  const registrationFormValid = Boolean(
    ticketTypeId &&
    guestForm.guestName.trim() &&
    enabledRegistrationFields.every((field) => !field.required || guestForm[field.key as keyof GuestForm].trim())
  );

  const optionalGuestFields = () => ({
    guestName: guestForm.guestName.trim(),
    guestNameKana: guestForm.guestNameKana.trim() || null,
    phone: guestForm.phone.trim() || null,
    email: guestForm.email.trim() || null,
    relationship: guestForm.relationship.trim() || null,
    affiliation: guestForm.affiliation.trim() || null,
    seatNote: guestForm.seatNote.trim() || null,
    accessibilityNote: guestForm.accessibilityNote.trim() || null,
    registrationNote: guestForm.registrationNote.trim() || null,
  });

  const submitRegistration = async () => {
    if (!registrationFormValid) return;
    setSaving(true);
    try {
      if (editingRegistrationId) {
        await updateEventGuestTicketRegistration(eventUuid, editingRegistrationId, {
          ...optionalGuestFields(),
          internalNote: internalNote.trim() || null,
        });
        toast.success('ゲスト情報を更新しました');
      } else {
        const matchingAllocation = dashboard?.allocations.find((allocation) => (
          allocation.enabled &&
          allocation.artistId === inviterArtistId &&
          allocation.ticketTypeId === Number(ticketTypeId)
        ));
        await createEventGuestTicketRegistration(eventUuid, {
          ...optionalGuestFields(),
          ticketTypeId: Number(ticketTypeId),
          eventPerformanceId: selectedTicket?.eventPerformanceId ?? null,
          kind,
          allocationId: matchingAllocation?.allocationId || null,
          inviterArtistId: inviterArtistId || null,
          internalNote: internalNote.trim() || null,
        });
        toast.success(kind === 'hold' ? '取り置きを登録しました' : '招待を登録しました');
      }
      resetRegistrationForm();
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '登録できませんでした');
    } finally {
      setSaving(false);
    }
  };

  const startRegistrationEdit = (registration: EventGuestTicketRegistration) => {
    setEditingRegistrationId(registration.registrationId);
    setKind(registration.kind);
    setTicketTypeId(String(registration.ticketTypeId));
    setInviterArtistId(registration.inviterArtistId || '');
    setGuestForm({
      guestName: registration.guestName || '',
      guestNameKana: registration.guestNameKana || '',
      phone: registration.phone || '',
      email: registration.email || '',
      relationship: registration.relationship || '',
      affiliation: registration.affiliation || '',
      seatNote: registration.seatNote || '',
      accessibilityNote: registration.accessibilityNote || '',
      registrationNote: registration.registrationNote || '',
    });
    setInternalNote(registration.internalNote || '');
  };

  const editAllocation = (allocation: EventGuestTicketAdminDashboard['allocations'][number]) => {
    setAllocationArtistId(allocation.artistId);
    setAllocationTicketTypeId(String(allocation.ticketTypeId));
    setHoldLimit(String(allocation.holdLimit));
    setInviteLimit(String(allocation.inviteLimit));
    setAllocationDeadline(toLocalDateTime(allocation.registrationDeadlineAt));
  };

  const toggleAllocation = async (allocation: EventGuestTicketAdminDashboard['allocations'][number]) => {
    setSaving(true);
    try {
      await upsertEventGuestTicketAllocation(eventUuid, {
        artistId: allocation.artistId,
        ticketTypeId: allocation.ticketTypeId,
        holdLimit: allocation.holdLimit,
        inviteLimit: allocation.inviteLimit,
        registrationDeadlineAt: allocation.registrationDeadlineAt,
        enabled: !allocation.enabled,
        allocationSource: allocation.allocationSource,
      });
      toast.success(allocation.enabled ? '参加者の受付を停止しました' : '参加者の受付を再開しました');
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '受付状態を変更できませんでした');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (
    registration: EventGuestTicketRegistration,
    status: 'paid' | 'used' | 'cancelled' | 'no_show',
    paidAmount?: number | null,
  ) => {
    setSaving(true);
    try {
      await changeEventGuestTicketRegistrationStatus(eventUuid, registration.registrationId, {
        status,
        paidAmount,
      });
      toast.success('状態を更新しました');
      await load();
      return true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '状態を更新できませんでした');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openPaymentModal = (registration: EventGuestTicketRegistration) => {
    setPaymentTarget(registration);
    setPaymentAmount(String(registration.ticketPrice || 0));
  };

  const confirmPayment = async () => {
    if (!paymentTarget) return;
    const amount = Number(paymentAmount);
    if (!Number.isInteger(amount) || amount < 0) {
      toast.error('金額は0以上の整数で入力してください');
      return;
    }
    if (await changeStatus(paymentTarget, 'paid', amount)) {
      setPaymentTarget(null);
    }
  };

  const confirmCancellation = async () => {
    if (!cancellationTarget) return;
    if (await changeStatus(cancellationTarget, 'cancelled')) {
      setCancellationTarget(null);
    }
  };

  const saveAllocation = async (allParticipants: boolean) => {
    if (!allocationTicketTypeId || (!allParticipants && !allocationArtistId)) return;
    const body = {
      ticketTypeId: Number(allocationTicketTypeId),
      holdLimit: Math.max(0, Number(holdLimit) || 0),
      inviteLimit: Math.max(0, Number(inviteLimit) || 0),
      registrationDeadlineAt: toOptionalIso(allocationDeadline),
      enabled: true,
    };
    setSaving(true);
    try {
      if (allParticipants) {
        await allocateEventGuestTicketsToAllParticipants(eventUuid, body);
      } else {
        await upsertEventGuestTicketAllocation(eventUuid, {
          ...body,
          artistId: allocationArtistId,
          allocationSource: 'individual',
        });
      }
      toast.success(allParticipants ? '参加者全員の枠を保存しました' : '参加者の枠を保存しました');
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '枠を保存できませんでした');
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    if (!dashboard) return;
    setSaving(true);
    try {
      const next = await updateEventGuestTicketSettings(eventUuid, {
        registrationDeadlineAt: toOptionalIso(settingsDeadline),
        formFields: settingsFields,
        defaultHoldLimit: dashboard.settings.defaultHoldLimit,
        defaultInviteLimit: dashboard.settings.defaultInviteLimit,
        allParticipantsEnabled: dashboard.settings.allParticipantsEnabled,
        expectedUpdatedAt: dashboard.settings.updatedAt || null,
      });
      setDashboard({ ...dashboard, settings: next });
      toast.success('入力項目と受付期限を保存しました');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '設定を保存できませんでした');
      await load();
    } finally {
      setSaving(false);
    }
  };

  const sendAnnouncement = async (deliveryScope: 'event_chat' | 'individual_chat') => {
    if (!dashboard) return;
    const artistIds = deliveryScope === 'event_chat'
      ? dashboard.eligibleParticipants.map((participant) => participant.artistId)
      : allocationArtistId ? [allocationArtistId] : [];
    if (!artistIds.length) {
      toast.error('案内先を選択してください');
      return;
    }
    setSaving(true);
    try {
      await publishEventGuestTicketAnnouncement(eventUuid, { deliveryScope, artistIds });
      toast.success(deliveryScope === 'event_chat' ? 'イベントチャットへ案内しました' : '個別チャットへ案内しました');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '案内を送信できませんでした');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DsButton
        variant="secondary"
        onClick={() => setOpen(true)}
        disabled={!canView}
        title={!canView ? 'このユーザー権限では取り置き・招待を表示できません。' : undefined}
      >
        取り置き・招待
      </DsButton>
      <DsModal
        open={open}
        onClose={() => setOpen(false)}
        title="取り置き・招待"
        ariaLabel="取り置き・招待"
        size="lg"
        bodyClassName="event-guest-ticket-modal"
        dismissible={!saving}
        footer={null}
      >
        {loading || !dashboard ? (
          <p role="status">取り置き・招待情報を読み込んでいます。</p>
        ) : (
          <div className="event-guest-ticket-modal__body">
            <div className="event-guest-ticket-summary" aria-label="取り置き・招待の集計">
              <div><span>販売済み</span><strong>{formatNumber(dashboard.summary.sold)}枚</strong></div>
              <div><span>未精算の取り置き</span><strong>{formatNumber(dashboard.summary.unpaidHolds)}枚</strong></div>
              <div><span>招待</span><strong>{formatNumber(dashboard.summary.invites)}枚</strong></div>
              <div><span>残り</span><strong>{formatNumber(dashboard.summary.remaining)}枚</strong></div>
            </div>

            <nav className="event-guest-ticket-tabs" aria-label="取り置き・招待の管理項目">
              <button type="button" className={tab === 'registrations' ? 'is-active' : ''} onClick={() => setTab('registrations')}>登録一覧</button>
              <button type="button" className={tab === 'allocations' ? 'is-active' : ''} onClick={() => setTab('allocations')}>参加者への枠</button>
              <button type="button" className={tab === 'settings' ? 'is-active' : ''} onClick={() => setTab('settings')}>入力項目</button>
            </nav>

            {tab === 'registrations' ? (
              <section className="event-guest-ticket-section">
                {canEdit ? (
                  <div className="event-guest-ticket-form">
                    <h3>{editingRegistrationId ? 'ゲスト情報を修正' : '取り置き・招待を登録'}</h3>
                    <div className="event-guest-ticket-form__grid">
                      <label>種類<select value={kind} onChange={(event) => setKind(event.target.value as 'hold' | 'invite')} disabled={Boolean(editingRegistrationId)}><option value="hold">取り置き（当日支払い）</option><option value="invite">招待（支払いなし）</option></select></label>
                      <label>券種<select value={ticketTypeId} onChange={(event) => setTicketTypeId(event.target.value)} disabled={Boolean(editingRegistrationId)}>{dashboard.summary.ticketTypes.map((ticket) => <option key={ticket.ticketTypeId} value={ticket.ticketTypeId}>{ticket.performanceName ? `${ticket.performanceName} / ` : ''}{ticket.ticketTypeName || `券種${ticket.ticketTypeId}`}</option>)}</select></label>
                      <label>招待者（任意）<select value={inviterArtistId} onChange={(event) => setInviterArtistId(event.target.value)} disabled={Boolean(editingRegistrationId)}><option value="">管理者登録</option>{dashboard.eligibleParticipants.map((participant) => <option key={participant.artistId} value={participant.artistId}>{participant.name}</option>)}</select></label>
                      {enabledRegistrationFields.map((field) => {
                        const key = field.key as keyof GuestForm;
                        return (
                          <label key={field.key} className={key.endsWith('Note') ? 'is-wide' : undefined}>
                            {FIELD_LABELS[key] || key}{field.required ? '（必須）' : '（任意）'}
                            <input
                              type={key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'}
                              value={guestForm[key]}
                              onChange={(event) => setGuestForm((current) => ({ ...current, [key]: event.target.value }))}
                              maxLength={key === 'registrationNote' ? 1000 : key.endsWith('Note') ? 500 : key === 'email' ? 320 : 200}
                              required={field.required}
                              placeholder={key === 'seatNote' ? '例：1階A列12番' : undefined}
                            />
                          </label>
                        );
                      })}
                      <label className="is-wide">管理者メモ（参加者には非表示）<input value={internalNote} onChange={(event) => setInternalNote(event.target.value)} maxLength={1000} /></label>
                    </div>
                    <div className="event-guest-ticket-form__actions">
                      {editingRegistrationId ? <DsButton variant="secondary" onClick={resetRegistrationForm} disabled={saving}>修正をやめる</DsButton> : null}
                      <DsButton onClick={() => void submitRegistration()} loading={saving} disabled={!registrationFormValid}>{editingRegistrationId ? '更新' : '登録'}</DsButton>
                    </div>
                  </div>
                ) : null}

                <div className="event-guest-ticket-list">
                  <div className="event-guest-ticket-list__header"><span>ゲスト</span><span>券種</span><span>状態</span><span>招待者・座席</span><span>操作</span></div>
                  {dashboard.registrations.map((registration) => (
                    <article key={registration.registrationId} className="event-guest-ticket-list__row">
                      <span><strong>{registration.guestName}</strong><small>{registration.phone || '電話番号なし'}</small></span>
                      <span>{registration.ticketTypeName || '-'}</span>
                      <span><i className={`is-${registration.status}`}>{STATUS_LABELS[registration.status] || registration.status}</i></span>
                      <span><small>{registration.inviterArtistName || '管理者登録'}</small><small>{registration.seatNote || '座席メモなし'}</small></span>
                      <span className="event-guest-ticket-list__actions">
                        {canEdit && !['paid', 'used', 'cancelled'].includes(registration.status) ? <DsButton variant="secondary" size="sm" onClick={() => startRegistrationEdit(registration)} disabled={saving}>修正</DsButton> : null}
                        {canEdit && registration.kind === 'hold' && ['reserved', 'no_show'].includes(registration.status) ? <DsButton size="sm" onClick={() => openPaymentModal(registration)} disabled={saving}>支払済み</DsButton> : null}
                        {canEdit && registration.kind === 'hold' && registration.status === 'reserved' ? <DsButton variant="secondary" size="sm" onClick={() => void changeStatus(registration, 'no_show')} disabled={saving}>未来場</DsButton> : null}
                        {canEdit && registration.kind === 'invite' && registration.status === 'registered' ? <DsButton size="sm" onClick={() => void changeStatus(registration, 'used')} disabled={saving}>来場済み</DsButton> : null}
                        {canEdit && !['cancelled'].includes(registration.status) ? <DsButton variant="danger" size="sm" onClick={() => setCancellationTarget(registration)} disabled={saving}>取消</DsButton> : null}
                      </span>
                    </article>
                  ))}
                  {!dashboard.registrations.length ? <p className="event-guest-ticket-empty">登録はまだありません。</p> : null}
                </div>
              </section>
            ) : null}

            {tab === 'allocations' ? (
              <section className="event-guest-ticket-section">
                <div className="event-guest-ticket-callout"><strong>参加者に登録枠を渡す</strong><p>枠は登録できる上限です。ゲストが登録されるまでは席を確保しません。</p></div>
                {canManageAllocations ? (
                  <div className="event-guest-ticket-form">
                    <div className="event-guest-ticket-form__grid">
                      <label>参加者<select value={allocationArtistId} onChange={(event) => setAllocationArtistId(event.target.value)}>{dashboard.eligibleParticipants.map((participant) => <option key={participant.artistId} value={participant.artistId}>{participant.name}</option>)}</select></label>
                      <label>券種<select value={allocationTicketTypeId} onChange={(event) => setAllocationTicketTypeId(event.target.value)}>{dashboard.summary.ticketTypes.map((ticket) => <option key={ticket.ticketTypeId} value={ticket.ticketTypeId}>{ticket.ticketTypeName || `券種${ticket.ticketTypeId}`}</option>)}</select></label>
                      <label>取り置き枠<input type="number" min="0" value={holdLimit} onChange={(event) => setHoldLimit(event.target.value)} /></label>
                      <label>招待枠<input type="number" min="0" value={inviteLimit} onChange={(event) => setInviteLimit(event.target.value)} /></label>
                      <label>受付期限（任意）<input type="datetime-local" value={allocationDeadline} onChange={(event) => setAllocationDeadline(event.target.value)} /></label>
                    </div>
                    <div className="event-guest-ticket-form__actions">
                      <DsButton onClick={() => void saveAllocation(false)} loading={saving}>この参加者へ設定</DsButton>
                      <DsButton variant="secondary" onClick={() => void saveAllocation(true)} disabled={saving}>参加者全員へ同じ枠を設定</DsButton>
                      <DsButton variant="secondary" onClick={() => void sendAnnouncement('individual_chat')} disabled={saving || !allocationArtistId}>この参加者へ個別案内</DsButton>
                      <DsButton variant="secondary" onClick={() => void sendAnnouncement('event_chat')} disabled={saving || !dashboard.eligibleParticipants.length}>イベントチャットへ全員に案内</DsButton>
                    </div>
                  </div>
                ) : null}
                <div className="event-guest-ticket-allocation-list">
                  {dashboard.allocations.map((allocation) => (
                    <article key={allocation.allocationId}>
                      <strong>{allocation.artistName}</strong>
                      <span>{allocation.ticketTypeName}</span>
                      <span>取り置き {allocation.holdUsed}/{allocation.holdLimit}</span>
                      <span>招待 {allocation.inviteUsed}/{allocation.inviteLimit}</span>
                      <span className="event-guest-ticket-allocation-list__actions">
                        <i>{allocation.enabled ? '受付中' : '停止中'}</i>
                        {canManageAllocations ? <DsButton size="sm" variant="secondary" onClick={() => editAllocation(allocation)} disabled={saving}>編集</DsButton> : null}
                        {canManageAllocations ? <DsButton size="sm" variant={allocation.enabled ? 'danger' : 'secondary'} onClick={() => void toggleAllocation(allocation)} disabled={saving}>{allocation.enabled ? '受付停止' : '再開'}</DsButton> : null}
                      </span>
                    </article>
                  ))}
                  {!dashboard.allocations.length ? <p className="event-guest-ticket-empty">参加者への枠はまだありません。</p> : null}
                </div>
              </section>
            ) : null}

            {tab === 'settings' ? (
              <section className="event-guest-ticket-section">
                <div className="event-guest-ticket-callout"><strong>参加者が入力する情報</strong><p>名前は常に必須です。個人情報はチャットや通知本文には表示しません。</p></div>
                <label className="event-guest-ticket-deadline">共通の受付期限<input type="datetime-local" value={settingsDeadline} onChange={(event) => setSettingsDeadline(event.target.value)} disabled={!canManageAllocations} /></label>
                <div className="event-guest-ticket-fields">
                  {settingsFields.map((field, index) => (
                    <div key={field.key}>
                      <strong>{FIELD_LABELS[field.key] || field.key}</strong>
                      <label><input type="checkbox" checked={field.enabled} disabled={!canManageAllocations || field.key === 'guestName'} onChange={(event) => setSettingsFields((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, enabled: event.target.checked, required: event.target.checked ? item.required : false } : item))} />使用する</label>
                      <label><input type="checkbox" checked={field.required} disabled={!canManageAllocations || !field.enabled || field.key === 'guestName'} onChange={(event) => setSettingsFields((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, required: event.target.checked } : item))} />必須</label>
                    </div>
                  ))}
                </div>
                {canManageAllocations ? <DsButton onClick={() => void saveSettings()} loading={saving}>入力項目を保存</DsButton> : null}
              </section>
            ) : null}
          </div>
        )}
      </DsModal>
      <DsModal
        open={Boolean(paymentTarget)}
        onClose={() => setPaymentTarget(null)}
        title="当日支払いを確定"
        ariaLabel="当日支払いを確定"
        size="sm"
        dismissible={!saving}
        bodyClassName="event-guest-ticket-confirmation"
        footer={(
          <DsButton onClick={() => void confirmPayment()} loading={saving}>
            支払いを確定
          </DsButton>
        )}
      >
        <p>
          {paymentTarget?.guestName}さんの「{paymentTarget?.ticketTypeName || 'チケット'}」を売上として確定します。
        </p>
        <label>
          受領金額
          <span className="event-guest-ticket-confirmation__amount">
            <span aria-hidden="true">¥</span>
            <input
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              value={paymentAmount}
              onChange={(event) => setPaymentAmount(event.target.value)}
              aria-label="受領金額"
            />
          </span>
        </label>
        <small>確定後は「取り置き（当日精算）」として券売・分析へ反映されます。</small>
      </DsModal>
      <DsModal
        open={Boolean(cancellationTarget)}
        onClose={() => setCancellationTarget(null)}
        title="登録を取り消しますか？"
        ariaLabel="登録を取り消しますか？"
        size="sm"
        dismissible={!saving}
        bodyClassName="event-guest-ticket-confirmation"
        footer={(
          <DsButton variant="danger" onClick={() => void confirmCancellation()} loading={saving}>
            取り消す
          </DsButton>
        )}
      >
        <p>
          {cancellationTarget?.guestName}さんの「{cancellationTarget?.ticketTypeName || 'チケット'}」を取り消します。
        </p>
        <small>
          {cancellationTarget?.status === 'paid'
            ? '支払済みの取り置きは、同額のマイナス調整を券売へ記録します。'
            : '取り消した枠は残席へ戻ります。'}
        </small>
      </DsModal>
    </>
  );
}
